import { Anchor, Badge, Flex, ThemeIcon, useMantineTheme } from '@mantine/core';
import { useMemo } from 'react';

import TRACKING_IDS from 'tracking-ids';

import { CitationCard } from './citation-card';
import {
  getCitationIcon,
  mapCitationSourceToCardData,
  resolveSourceType,
} from './citation-card/CitationCard.utils';
import CitationPopover from './CitationPopover';
import { useMarkdownRendererContext } from './markdown-renderer/MarkdownRenderer.context';

import type { CitationSource } from '../types';
import type { Icon } from '@phosphor-icons/react';

type CitationItemProps = {
  source: CitationSource;
  icon: Icon;
  title: string;
  snippetId: string | null;
  /** When set, renders a numbered badge instead of the icon */
  number?: number;
  /** Handler for removing the source from the answer. Only shown for active sources. */
  onRemove?: () => void;
};

function DefaultItemContent({
  source,
  onRemove,
}: {
  source: CitationSource;
  onRemove?: () => void;
}) {
  const cardData = useMemo(() => {
    const { authorId: _, ...data } = mapCitationSourceToCardData(source);
    return data;
  }, [source]);

  return <CitationCard {...cardData} onRemove={onRemove} />;
}

export default function CitationItem({
  source,
  icon: IconComponent,
  title,
  snippetId,
  number,
  onRemove,
}: CitationItemProps) {
  const { renderCitationContentOverride } = useMarkdownRendererContext();
  const { primaryColor } = useMantineTheme();

  const sourceType = resolveSourceType(
    source.citation_data.source_name ?? source.citation_data.source_type,
    source.url,
  );

  const ResolvedIcon = getCitationIcon(sourceType);
  const FinalIcon = sourceType ? ResolvedIcon : IconComponent;

  const anchorContent = (
    <Anchor
      size="sm"
      mt={-1}
      fw={500}
      lineClamp={2}
      data-tracking-id={TRACKING_IDS.hatch.chat.sources.viewIndividual}
    >
      {title}
    </Anchor>
  );

  const overrideContent = renderCitationContentOverride?.({
    source,
    onRemove,
  });

  const content =
    overrideContent != null ? (
      overrideContent
    ) : (
      <DefaultItemContent source={source} onRemove={onRemove} />
    );

  const indicator =
    number !== undefined ? (
      <Badge
        variant="light"
        color={primaryColor}
        style={{ cursor: 'pointer', flexShrink: 0 }}
      >
        {number}
      </Badge>
    ) : (
      <ThemeIcon variant="subtle" size={18} style={{ '--ti-radius': '4px' }}>
        <FinalIcon size={14} />
      </ThemeIcon>
    );

  return (
    <Flex wrap="nowrap" gap="xs" align="flex-start">
      {indicator}
      <CitationPopover content={content} width={360}>
        {anchorContent}
      </CitationPopover>
    </Flex>
  );
}
